/**
 * Vastu direction utilities for planetary analysis
 */

import { PlanetName } from './astroEngine';

export type VastuDirection = 
  | 'East' 
  | 'Southeast' 
  | 'South' 
  | 'Southwest' 
  | 'West' 
  | 'Northwest' 
  | 'North' 
  | 'Northeast' 
  | 'Center';

/**
 * Planetary direction mappings based on Vastu principles
 */
export const PLANET_DIRECTIONS: Record<PlanetName, VastuDirection[]> = {
  Sun: ['East'],
  Moon: ['Northwest', 'North'],
  Mars: ['South'],
  Mercury: ['North'],
  Jupiter: ['Northeast'],
  Venus: ['Southeast'],
  Saturn: ['West', 'Southwest'],
  Rahu: ['Southwest'],
  Ketu: ['Center'],
};

/**
 * Get the primary direction for a planet
 */
export function getPlanetDirection(planet: PlanetName): VastuDirection {
  return PLANET_DIRECTIONS[planet]?.[0] || 'Center';
}

/**
 * Get all directions for a planet (some planets have multiple)
 */
export function getPlanetDirections(planet: PlanetName): VastuDirection[] {
  return PLANET_DIRECTIONS[planet] || ['Center'];
}

/**
 * Determine the best planet in a house based on:
 * 1. Functional benefic status
 * 2. Exaltation
 * 3. Shadbala percentage
 * 4. Blended percentage
 */
export function selectBestPlanet(
  planets: PlanetName[],
  options: {
    functionalBenefics?: Set<PlanetName>;
    functionalMalefics?: Set<PlanetName>;
    exaltedPlanets?: Record<string, boolean>;
    shadbalaScores?: Partial<Record<PlanetName, number>>;
    blendedScores?: Partial<Record<PlanetName, number>>;
  }
): PlanetName | null {
  if (planets.length === 0) return null;
  if (planets.length === 1) return planets[0];

  const { functionalBenefics, functionalMalefics, exaltedPlanets, shadbalaScores, blendedScores } = options;

  // Score each planet
  const scores = planets.map(planet => {
    let score = 0;

    // Functional benefic: +1000 points (highest priority)
    if (functionalBenefics?.has(planet)) {
      score += 1000;
    }

    // Functional malefic: -500 points (avoid selection if any neutral/benefic exists)
    if (functionalMalefics?.has(planet)) {
      score -= 500;
    }

    // Exalted: +500 points
    if (exaltedPlanets?.[planet]) {
      score += 500;
    }

    // Shadbala: add percentage (0-100)
    if (shadbalaScores?.[planet] !== undefined) {
      score += shadbalaScores[planet] * 2; // Weight shadbala higher
    }

    // Blended: add percentage (0-100)
    if (blendedScores?.[planet] !== undefined) {
      score += blendedScores[planet];
    }

    return { planet, score };
  });

  // Sort by score descending
  scores.sort((a, b) => b.score - a.score);

  return scores[0].planet;
}

/**
 * Get direction for a house based on occupants or lord
 */
export function getHouseDirection(
  houseNum: number,
  placements: Record<string, number>,
  lordPlanet: PlanetName,
  options: {
    functionalBenefics?: Set<PlanetName>;
    exaltedPlanets?: Record<string, boolean>;
    shadbalaScores?: Partial<Record<PlanetName, number>>;
    blendedScores?: Partial<Record<PlanetName, number>>;
  }
): { 
  direction: VastuDirection; 
  source: 'occupant' | 'lord'; 
  planet: PlanetName;
  allDirections: VastuDirection[];
} {
  // Find all planets in this house
  const occupants = Object.entries(placements)
    .filter(([_, house]) => house === houseNum)
    .map(([planet, _]) => planet as PlanetName);

  let selectedPlanet: PlanetName;
  let source: 'occupant' | 'lord';

  if (occupants.length > 0) {
    // House has occupants - select best one
    selectedPlanet = selectBestPlanet(occupants, options) || occupants[0];
    source = 'occupant';
  } else {
    // Empty house - use lord
    selectedPlanet = lordPlanet;
    source = 'lord';
  }

  const allDirections = getPlanetDirections(selectedPlanet);
  const primaryDirection = allDirections[0];

  return {
    direction: primaryDirection,
    source,
    planet: selectedPlanet,
    allDirections,
  };
}

/**
 * Life area to house mappings (based on traditional Bhava associations)
 */
export const LIFE_AREA_HOUSES: Record<string, number[]> = {
  'Self & Personality': [1],
  'Wealth & Family': [2],
  'Siblings & Communication': [3],
  'Home & Mother': [4],
  'Children & Creativity': [5],
  'Health & Service': [6],
  'Marriage & Partnership': [7],
  'Longevity & Transformation': [8],
  'Fortune & Dharma': [9],
  'Career & Status': [10],
  'Gains & Friendships': [11],
  'Spirituality & Loss': [12],
};

/**
 * Get Vastu direction recommendations for life areas
 */
export function getLifeAreaDirections(
  placements: Record<string, number>,
  houseLords: Record<number, PlanetName>,
  options: {
    functionalBenefics?: Set<PlanetName>;
    exaltedPlanets?: Record<string, boolean>;
    shadbalaScores?: Partial<Record<PlanetName, number>>;
    blendedScores?: Partial<Record<PlanetName, number>>;
  }
): Record<string, {
  houses: number[];
  lordDirections: Array<{ house: number; lord: PlanetName; direction: VastuDirection }>;
  occupantDirections: Array<{ house: number; planet: PlanetName; direction: VastuDirection }>;
  primaryDirection: VastuDirection;
}> {
  const result: Record<string, any> = {};

  for (const [lifeArea, houses] of Object.entries(LIFE_AREA_HOUSES)) {
    const lordDirections: Array<{ house: number; lord: PlanetName; direction: VastuDirection }> = [];
    const occupantDirections: Array<{ house: number; planet: PlanetName; direction: VastuDirection }> = [];
    const allOccupants: PlanetName[] = [];
    const allLords: PlanetName[] = [];

    for (const houseNum of houses) {
      const lord = houseLords[houseNum];
      if (lord) {
        const lordDir = getPlanetDirection(lord);
        lordDirections.push({ house: houseNum, lord, direction: lordDir });
        allLords.push(lord);
      }

      // Collect all occupants in this house
      const houseOccupants = Object.entries(placements)
        .filter(([, h]) => h === houseNum)
        .map(([p]) => p as PlanetName);
      houseOccupants.forEach(occ => {
        allOccupants.push(occ);
        occupantDirections.push({
          house: houseNum,
          planet: occ,
          direction: getPlanetDirection(occ),
        });
      });
    }

    // Primary direction: occupant best-planet wins over lord best-planet
    let primaryDirection: VastuDirection;
    if (allOccupants.length > 0) {
      const best = selectBestPlanet(allOccupants, options) || allOccupants[0];
      primaryDirection = getPlanetDirection(best);
    } else if (allLords.length > 0) {
      const best = selectBestPlanet(allLords, options) || allLords[0];
      primaryDirection = getPlanetDirection(best);
    } else {
      primaryDirection = 'Center';
    }

    result[lifeArea] = {
      houses,
      lordDirections,
      occupantDirections,
      primaryDirection,
    };
  }

  return result;
}
